import React from "../../__compat__/react/main.entry.mjs.mjs";
import { SvgIconNames } from "../Icon/_SvgIconNames.generated.mjs.mjs";
import { Loader } from "../Loader/index.mjs.mjs";
import { useSpacing } from "../../api/spacing/useSpacing.mjs.mjs";
import { pickDataProps, pickAriaProps, pickComponentCommonProps, } from "../../interfaces/commonProps.mjs.mjs";
import { useTransitionEvent } from "../../hooks/useTransitionEvent.mjs.mjs";
import { iconsNames } from "../Icon/IconsNames.mjs.mjs";
import { Icon } from "../index.mjs.mjs";
import { StyledButton, StyledArrow, StyledButtonLabel, StyledTransitionContainer, StyledIconContainer, } from "./Button.styles.mjs.mjs";
import { IconButton } from "./IconButton.mjs.mjs";
/**
 * Buttons can be used to show the user’s choice of options for actions and assign these to a clear hierarchy.
 */
const iconElement = (icon) => {
    return typeof icon === 'string' ? (React.createElement(Icon, { size: "small", name: icon })) : (React.createElement(StyledIconContainer, null, icon));
};
function createButtonProps(buttonProps, defaultVariant, children) {
    const props = {
        size: buttonProps.size || 'medium',
        variant: buttonProps.variant || defaultVariant,
        isLoading: buttonProps.loading || false,
        elevated: buttonProps.elevated || false,
        stretch: buttonProps.stretch || false,
        newWindow: buttonProps.newWindow || false,
        disabled: buttonProps.disabled || false,
        href: !buttonProps.disabled ? buttonProps.href : undefined,
        label: buttonProps.label,
        icon: buttonProps.icon,
        onClick: buttonProps.onClick,
        children,
        role: buttonProps.role,
        rel: buttonProps.rel,
        ...pickComponentCommonProps(buttonProps),
        ...pickDataProps(buttonProps),
        ...pickAriaProps(buttonProps),
        ...(buttonProps.href
            ? {
                as: 'a',
                download: buttonProps.download,
                ...(buttonProps.newWindow && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                }),
                // Override the 'target' attribute if a user value was provided.
                // This has priority over any values derived from other properties (e.g. 'newWindow' prop).
                ...(buttonProps.target !== undefined && {
                    target: buttonProps.target,
                }),
            }
            : {
                disabled: buttonProps.disabled || false,
                name: buttonProps.name,
                type: buttonProps.type || 'button',
                value: buttonProps.value,
            }),
    };
    props.isLoading = props.variant !== 'text' && props.isLoading;
    props.disabled = props.isLoading || props.disabled;
    props.elevated = props.elevated || false;
    return { ...props };
}
function isValidIconName(value) {
    return [...iconsNames, ...SvgIconNames].includes(value);
}
export const Button = React.forwardRef(function Button(props, ref) {
    const btnProps = createButtonProps(props, 'primary', props.children);
    const [loaderVisible, setLoaderVisible] = React.useState(btnProps.isLoading);
    const handleTransitionEvent = React.useCallback((e) => {
        if (e.currentTarget) {
            const opacity = getComputedStyle(e.currentTarget).opacity;
            setLoaderVisible(opacity !== '0');
        }
    }, [setLoaderVisible]);
    const transitionElementRef = useTransitionEvent(handleTransitionEvent);
    const outerSpacing = useSpacing(props);
    if (btnProps.icon &&
        !React.isValidElement(btnProps.icon) &&
        !isValidIconName(btnProps.icon)) {
        console.error(`Invalid icon name: ${btnProps.icon}`);
        return null;
    }
    if (btnProps.variant.includes('icon')) {
        return React.createElement(IconButton, { ref: ref, ...btnProps, spacing: outerSpacing });
    }
    return (React.createElement(StyledButton, { ref: ref, ...btnProps, outerSpacing: outerSpacing },
        React.createElement(StyledTransitionContainer, { overlay: true, visible: btnProps.isLoading, ref: transitionElementRef, "aria-hidden": true }, loaderVisible && (React.createElement(Loader, { monochrome: true, size: "small", inverted: btnProps.variant === 'primary' }))),
        React.createElement(StyledTransitionContainer, { overlay: false, visible: !btnProps.isLoading },
            btnProps.icon && iconElement(btnProps.icon),
            React.createElement(StyledButtonLabel, { size: btnProps.size, icon: !!btnProps.icon }, btnProps.children),
            !btnProps.icon && btnProps.variant === 'text' && (React.createElement(StyledArrow, { xmlns: "http://www.w3.org/2000/svg", width: "8", height: "13", viewBox: "0 0 8 13", "aria-hidden": true },
                React.createElement("path", { stroke: "currentColor", strokeWidth: "1px", fill: "none", d: "M1.5,1l5.485,5.504l-5.48,5.496" }))))));
});
//# sourceMappingURL=Button.mjs.mjs.map